import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Comment as CommentIcon,
  Person as PersonIcon,
  LinkedIn as LinkedInIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Reply as ReplyIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { formatDistanceToNow, format } from 'date-fns';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.instation.app';

/**
 * Extract a numeric LinkedIn post ID from various URL formats:
 *  - /posts/...-activity-<id>-XXXX/
 *  - /feed/update/urn:li:ugcPost:<id>/
 *  - /feed/update/urn:li:share:<id>/
 */
const extractPostId = (url) => {
  if (!url) return null;
  const activityMatch = url.match(/activity-(\d+)/);
  if (activityMatch) return activityMatch[1];
  const urnMatch = url.match(/urn:li:(?:ugcPost|share):(\d+)/);
  if (urnMatch) return urnMatch[1];
  // bare numeric
  const numericMatch = url.match(/\/(\d{15,})/);
  if (numericMatch) return numericMatch[1];
  return null;
};

const CommentAssistant = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // URL input
  const [postUrl, setPostUrl] = useState('');
  const [urlError, setUrlError] = useState('');

  // Selected post (minimal object built from URL)
  const [selectedPost, setSelectedPost] = useState(null);

  // Comments
  const [fetchingComments, setFetchingComments] = useState(false);
  const [postComments, setPostComments] = useState({});

  // Comment dialog
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentingPost, setCommentingPost] = useState(null);
  const [commentLoading, setCommentLoading] = useState(false);

  // Reply dialog
  const [openReplyDialog, setOpenReplyDialog] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyLoading, setReplyLoading] = useState(false);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const diffInHours = Math.abs(new Date() - date) / 36e5;
    if (diffInHours < 24) return formatDistanceToNow(date, { addSuffix: true });
    if (diffInHours < 48) return 'yesterday';
    return format(date, 'MMM d, yyyy h:mm a');
  };

  const handleLoadPost = async () => {
    setUrlError('');
    const postId = extractPostId(postUrl.trim());
    if (!postId) {
      setUrlError('Could not parse a LinkedIn post ID from this URL. Please paste a valid LinkedIn post URL.');
      return;
    }
    const post = {
      id: postId,
      urn: `urn:li:ugcPost:${postId}`,
      url: postUrl.trim(),
      stats: { comments: 0 }
    };
    setSelectedPost(post);
    await fetchPostComments(postId, post);
  };

  const fetchPostComments = async (postId, post) => {
    try {
      setFetchingComments(true);
      const shortPostId = postId.includes(':') ? postId.split(':').pop() : postId;
      const response = await axios.get(
        `${API_URL}/api/linkedin/post-comments`,
        { params: { postId: shortPostId }, headers: authHeader(), timeout: 30000 }
      );
      let commentsData = [];
      if (response.data?.success === 1 && Array.isArray(response.data.results)) {
        commentsData = response.data.results;
      }
      const comments = commentsData.map(c => ({
        id: c.id || uuidv4(),
        text: c.comment || '',
        author: { name: c.profileFullName || 'LinkedIn User', headline: c.profileHeadline || '' },
        createdAt: c.timestamp || new Date().toISOString(),
        urn: c.urn || c.id || ''
      }));
      const key = postId;
      setPostComments(prev => ({ ...prev, [key]: comments }));
      if (post) {
        setSelectedPost(prev => ({ ...prev, stats: { ...prev?.stats, comments: comments.length } }));
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      enqueueSnackbar('Could not load comments. Please try again.', { variant: 'error' });
    } finally {
      setFetchingComments(false);
    }
  };

  // Comment dialog
  const handleOpenCommentDialog = (post) => { setCommentingPost(post); setCommentText(''); setOpenCommentDialog(true); };
  const handleCloseCommentDialog = () => { setOpenCommentDialog(false); setCommentingPost(null); setCommentText(''); };

  const handlePostComment = async () => {
    if (!commentText.trim()) { enqueueSnackbar('Please enter a comment', { variant: 'warning' }); return; }
    if (commentText.length > 700) { enqueueSnackbar('Comment is too long (max 700 characters)', { variant: 'warning' }); return; }
    setCommentLoading(true);
    try {
      const activityUrn = commentingPost.urn || commentingPost.id;
      const response = await axios.post(
        `${API_URL}/api/linkedin/post-comment`,
        { activityUrn, comment: commentText },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        enqueueSnackbar('Comment posted to LinkedIn!', { variant: 'success' });
        const key = commentingPost.id;
        setPostComments(prev => ({
          ...prev,
          [key]: [...(prev[key] || []), {
            id: Date.now().toString(),
            text: commentText,
            author: { name: user?.name || 'You', headline: user?.headline || '' },
            createdAt: new Date().toISOString()
          }]
        }));
        setSelectedPost(prev => ({ ...prev, stats: { ...prev.stats, comments: (prev.stats.comments || 0) + 1 } }));
        handleCloseCommentDialog();
      } else {
        throw new Error(response.data.message || 'Failed to post comment');
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || err.message || 'Failed to post comment.', { variant: 'error' });
    } finally {
      setCommentLoading(false);
    }
  };

  // Reply dialog
  const handleOpenReplyDialog = (comment) => { setReplyingTo(comment); setReplyText(''); setOpenReplyDialog(true); };
  const handleCloseReplyDialog = () => { setOpenReplyDialog(false); setReplyingTo(null); setReplyText(''); };

  const handlePostReply = async () => {
    if (!replyText.trim()) { enqueueSnackbar('Please enter a reply', { variant: 'warning' }); return; }
    setReplyLoading(true);
    try {
      const postUrn = selectedPost.id.includes('ugcPost:') ? selectedPost.id : `urn:li:ugcPost:${selectedPost.id}`;
      const commentId = replyingTo.id.includes('comment:') ? replyingTo.id.split('comment:')[1].replace(/[()]/g, '') : replyingTo.id;
      const response = await axios.post(
        `${API_URL}/api/linkedin/post-reply`,
        { postUrn, parentCommentId: commentId, message: { text: replyText } },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        enqueueSnackbar('Reply posted to LinkedIn!', { variant: 'success' });
        setPostComments(prev => ({
          ...prev,
          [selectedPost.id]: [...(prev[selectedPost.id] || []), {
            id: response.data.replyUrn || `reply-${Date.now()}`,
            text: replyText,
            author: { name: user?.name || 'You', headline: user?.headline || '' },
            createdAt: new Date().toISOString(),
            parentCommentId: replyingTo.id
          }]
        }));
        handleCloseReplyDialog();
      } else {
        throw new Error(response.data.message || 'Failed to post reply');
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || err.message || 'Failed to post reply.', { variant: 'error' });
    } finally {
      setReplyLoading(false);
    }
  };

  const comments = selectedPost ? (postComments[selectedPost.id] || []) : [];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CommentIcon sx={{ color: '#0A66C2', mr: 1.5, fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>Comment Assistant</Typography>
            <Typography variant="body2" color="text.secondary">
              Paste a LinkedIn post URL to view and reply to comments using AI.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Paste a LinkedIn post URL, e.g. https://www.linkedin.com/posts/..."
            value={postUrl}
            onChange={e => { setPostUrl(e.target.value); setUrlError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLoadPost()}
            error={!!urlError}
            helperText={urlError}
            InputProps={{ startAdornment: <LinkedInIcon sx={{ color: '#0A66C2', mr: 1, fontSize: 20 }} /> }}
          />
          <Button
            variant="contained"
            onClick={handleLoadPost}
            startIcon={<SearchIcon />}
            sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, bgcolor: '#0A66C2', '&:hover': { bgcolor: '#004182' } }}
          >
            Load Comments
          </Button>
        </Box>
      </Paper>

      {/* Comments panel */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', minHeight: 400 }}>
        {!selectedPost ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 1 }}>
            <CommentIcon sx={{ fontSize: 48, color: '#CBD5E0', mb: 1 }} />
            <Typography color="text.secondary">Paste a LinkedIn post URL above to get started.</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            {/* Post link + comment button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Comments {fetchingComments ? '' : `(${comments.length})`}
                </Typography>
                <Tooltip title="Open post on LinkedIn">
                  <Typography
                    component="a"
                    href={selectedPost.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ color: '#0A66C2', display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    <LinkedInIcon sx={{ fontSize: 16 }} />
                    View post on LinkedIn
                  </Typography>
                </Tooltip>
              </Box>
              <Button
                variant="contained"
                startIcon={<CommentIcon />}
                onClick={() => handleOpenCommentDialog(selectedPost)}
                sx={{ textTransform: 'none', fontWeight: 500, bgcolor: '#0A66C2', '&:hover': { bgcolor: '#004182' }, borderRadius: 1.5 }}
              >
                Add Comment
              </Button>
            </Box>

            {fetchingComments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={32} sx={{ color: '#0A66C2' }} />
              </Box>
            ) : comments.length > 0 ? (
              <List disablePadding>
                {comments.map((comment, index) => (
                  <React.Fragment key={`${comment.id}-${index}`}>
                    <ListItem sx={{ px: 0, py: 2, alignItems: 'flex-start' }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#0A66C2' }}>
                          {comment.author.name?.[0] || <PersonIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle2" fontWeight={600}>{comment.author.name}</Typography>
                            {comment.author.headline && (
                              <Typography variant="caption" color="text.secondary">{comment.author.headline}</Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, mb: 1 }}>{comment.text}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">{formatDate(comment.createdAt)}</Typography>
                              <Button
                                size="small"
                                startIcon={<ReplyIcon fontSize="small" />}
                                onClick={() => handleOpenReplyDialog(comment)}
                                sx={{ textTransform: 'none', color: '#0A66C2', minWidth: 0, p: '2px 8px', '&:hover': { bgcolor: 'rgba(10,102,194,0.04)' } }}
                              >
                                Reply
                              </Button>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < comments.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">No comments found for this post.</Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Comment Dialog */}
      {commentingPost && (
        <Dialog open={openCommentDialog} onClose={handleCloseCommentDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
            <Typography variant="h6">Add Comment</Typography>
            <IconButton size="small" onClick={handleCloseCommentDialog}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              multiline rows={4} fullWidth autoFocus
              placeholder="Write your comment..."
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              inputProps={{ maxLength: 700 }}
              helperText={`${commentText.length}/700`}
              FormHelperTextProps={{ sx: { textAlign: 'right' } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCommentDialog} disabled={commentLoading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePostComment}
              disabled={commentLoading || !commentText.trim()}
              startIcon={commentLoading ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ bgcolor: '#0A66C2', '&:hover': { bgcolor: '#004182' }, textTransform: 'none' }}
            >
              {commentLoading ? 'Posting…' : 'Post Comment'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Reply Dialog */}
      {replyingTo && (
        <Dialog open={openReplyDialog} onClose={handleCloseReplyDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
            <Typography variant="h6">Reply to {replyingTo.author.name}</Typography>
            <IconButton size="small" onClick={handleCloseReplyDialog}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">{replyingTo.text}</Typography>
            </Paper>
            <TextField
              multiline rows={3} fullWidth autoFocus
              placeholder="Write your reply..."
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseReplyDialog} disabled={replyLoading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePostReply}
              disabled={replyLoading || !replyText.trim()}
              startIcon={replyLoading ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ bgcolor: '#0A66C2', '&:hover': { bgcolor: '#004182' }, textTransform: 'none' }}
            >
              {replyLoading ? 'Posting…' : 'Post Reply'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default CommentAssistant;
